import { A } from '@solidjs/router';
import { createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { locale, t } from '@/shared/i18n';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import FeatureMarketingLayout from './components/FeatureMarketingLayout';
import FeatureFaqSection from './components/FeatureFaqSection';

export default function AiAnalyticsPage() {
  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'khoai.io — AI & Analytics',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          url: 'https://khoai.io/features/ai-analytics',
          description:
            'AI-powered demand forecasting, smart stockout alerts, supplier reliability scoring, and one-click restock recommendations for warehouse and store operators.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: {
            '@type': 'Organization',
            name: 'khoai.io',
            url: 'https://khoai.io',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://khoai.io',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'AI & Analytics',
              item: 'https://khoai.io/features/ai-analytics',
            },
          ],
        },
      ],
    };
    let script = document.getElementById(
      'ld-json-feature-ai-analytics'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-feature-ai-analytics';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    onCleanup(() =>
      document.getElementById('ld-json-feature-ai-analytics')?.remove()
    );
  });

  const [revenueImageFailed, setRevenueImageFailed] = createSignal(false);
  const [forecastImageFailed, setForecastImageFailed] = createSignal(false);
  const [alertsImageFailed, setAlertsImageFailed] = createSignal(false);
  const localizedRevenueImage = createMemo(() =>
    locale() === 'vi'
      ? '/revenue_cost_trend_vn.jpg'
      : '/revenue_cost_trend_en.jpg'
  );
  const localizedForecastImage = createMemo(() =>
    locale() === 'vi'
      ? '/inventory_risk_forecast_vn.jpg'
      : '/inventory_risk_forecast_en.jpg'
  );
  const localizedAlertsImage = createMemo(() =>
    locale() === 'vi' ? '/low_stock_alerts_vn.jpg' : '/low_stock_alerts_en.jpg'
  );
  return (
    <FeatureMarketingLayout
      pageTag={t('feature.analytics.tag')}
      title={t('feature.analytics.title')}
      subtitle={t('feature.analytics.subtitle')}
      lightTheme
      heroRevealOnScroll
    >
      {/* -- Section 1: Revenue & cost trend -- */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.analytics.f0')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.analytics.f0Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.analytics.f0Bullet1')}</li>
              <li>{t('feature.analytics.f0Bullet2')}</li>
              <li>{t('feature.analytics.f0Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!revenueImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  Revenue &amp; cost trend screenshot
                </div>
              }
            >
              <img
                src={localizedRevenueImage()}
                alt={t('feature.analytics.f0')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setRevenueImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      {/* ── Section 2: Feature 1 ── */}
      <section class="bg-white py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.28fr_0.72fr] lg:items-center lg:px-8">
          <div class="order-2 p-0 sm:p-2 lg:order-1 lg:pr-2">
            <Show
              when={!forecastImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.analytics.visual2Caption')}
                </div>
              }
            >
              <img
                src={localizedForecastImage()}
                alt={t('feature.analytics.visual2Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setForecastImageFailed(true)}
              />
            </Show>
          </div>
          <div class="order-1 p-2 sm:p-4 lg:order-2 lg:pl-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.analytics.f1')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.analytics.f1Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.analytics.f1Bullet1')}</li>
              <li>{t('feature.analytics.f1Bullet2')}</li>
              <li>{t('feature.analytics.f1Bullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Section 3: Feature 2 ── */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.analytics.f2')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.analytics.f2Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.analytics.f2Bullet1')}</li>
              <li>{t('feature.analytics.f2Bullet2')}</li>
              <li>{t('feature.analytics.f2Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!alertsImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.analytics.visual3Caption')}
                </div>
              }
            >
              <img
                src={localizedAlertsImage()}
                alt={t('feature.analytics.visual3Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setAlertsImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      <FeatureFaqSection
        titleKey="feature.analytics.faq.title"
        scriptId="ld-json-analytics-faq"
        items={[
          {
            q: t('feature.analytics.faq.q1'),
            a: t('feature.analytics.faq.a1'),
          },
          {
            q: t('feature.analytics.faq.q2'),
            a: t('feature.analytics.faq.a2'),
          },
          {
            q: t('feature.analytics.faq.q3'),
            a: t('feature.analytics.faq.a3'),
          },
          {
            q: t('feature.analytics.faq.q4'),
            a: t('feature.analytics.faq.a4'),
          },
          {
            q: t('feature.analytics.faq.q5'),
            a: t('feature.analytics.faq.a5'),
          },
        ]}
      />

      <section class="bg-white py-16">
        <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 class="text-4xl font-black text-slate-900">
            {t('feature.analytics.cta')}
          </h2>
          <p class="mx-auto mt-4 max-w-2xl text-base text-slate-700">
            {t('feature.analytics.ctaSubtitle')}
          </p>
          <A
            href="/signup"
            onClick={() =>
              trackMarketingEvent('feature_cta_click', {
                feature: 'ai-analytics',
                target: '/signup',
              })
            }
            class="mt-8 inline-block rounded-xl bg-amber-600 px-8 py-3.5 text-lg font-bold text-white hover:bg-amber-700"
          >
            {t('feature.cta')}
          </A>
        </div>
      </section>
    </FeatureMarketingLayout>
  );
}
